import React, { useRef, useEffect, useImperativeHandle, forwardRef } from "react";
import { Container } from "@pixi/react";
import ReelFrameDecorationController from "./ReelFrameDecorationController";
import { safeInit } from "../../../utilities";

/**
 * ReelFrameDecoration Component
 * Displays decorative spine animations (claws) on the reel frame during Hold and Win
 * Switches between portrait and landscape animations based on orientation
 * Note: Offsets are handled in the controller based on symbol size
 */
const ReelFrameDecoration = forwardRef(({
  symbolWidth,
  symbolHeight,
  dimensions,
  dividerDimensions,
  orientation,
}, ref) => {
  const decorationControllerRef = useRef(null);
  const decorationContainerRef = useRef(null);

  // Expose controller methods to parent
  useImperativeHandle(ref, () => ({
    playEndAnimation: () => {
      return decorationControllerRef.current?.playEndAnimation?.() || Promise.resolve();
    },
  }));

  // Initialize controller when assets are ready
  useEffect(() => {
    if (decorationContainerRef.current && !decorationControllerRef.current) {
      safeInit(() => {
        decorationControllerRef.current = new ReelFrameDecorationController(
          decorationContainerRef.current,
          symbolWidth,
          symbolHeight,
          dimensions,
          dividerDimensions,
          orientation
        );
      });
    }

    return () => {
      if (decorationControllerRef.current) {
        decorationControllerRef.current.cleanup?.();
        decorationControllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle dimension and orientation changes
  useEffect(() => {
    if (
      decorationControllerRef.current &&
      symbolWidth &&
      symbolHeight &&
      dimensions
    ) {
      decorationControllerRef.current.resizeDecoration(
        symbolWidth,
        symbolHeight,
        dimensions,
        dividerDimensions,
        orientation
      );
    }
  }, [symbolWidth, symbolHeight, dimensions, dividerDimensions, orientation]);

  return (
    <Container
      ref={decorationContainerRef}
      name="reel_frame_decoration"
      y={0}
      x={0}
      sortableChildren
      zIndex={7}
    />
  );
});

ReelFrameDecoration.displayName = "ReelFrameDecoration";

export default ReelFrameDecoration;
